/**
 * VIP Timer Component
 * Handles time-limited product availability with countdown timers
 */

/**
 * Check if dev mode is enabled (for conditional logging)
 * Can be enabled via localStorage: localStorage.setItem('vip-timer-debug', 'true')
 * Or URL param: ?vip-debug=true
 */
function isDevMode() {
  // Check localStorage
  try {
    if (localStorage.getItem('vip-timer-debug') === 'true') {
      return true;
    }
  } catch (e) {
    // localStorage not available
  }
  
  // Check URL param
  if (typeof URLSearchParams !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    if (params.get('vip-debug') === 'true') {
      return true;
    }
  }
  
  return false;
}

/**
 * Conditional console.log - only logs in dev mode
 */
function debugLog(...args) {
  if (isDevMode()) {
    console.log(...args);
  }
}

class VipTimer {
  constructor(element, options = {}) {
    this.element = element;
    this.product = options.product;
    this.storeTimezone = options.storeTimezone || 'Europe/London'; // Default to UK timezone
    this.isCart = options.isCart || false;
    this.updateInterval = null;
    this.isVisible = true;
    
    debugLog('VipTimer constructor called with:', { element, options });
    debugLog('Product data:', this.product);
    debugLog('Store timezone:', this.storeTimezone);
    debugLog('Is cart mode:', this.isCart);
    
    // Initialize
    this.init();
  }
  
  /**
   * Get current VIP stock for today from global pool
   */
  async getCurrentStock() {
    // Read from data attributes passed from Liquid
    const poolSold = parseInt(this.element.dataset.poolSold || '0');
    const poolTotal = parseInt(this.element.dataset.poolTotal || '5');
    const remaining = Math.max(0, poolTotal - poolSold);
    
    // Store global pool total for use in renderAvailability
    this.globalPoolTotal = poolTotal;
    
    debugLog(`Global VIP pool: ${poolSold}/${poolTotal} sold, ${remaining} remaining`);
    
    if (remaining >= 0) {
      return remaining;
    }
    
    // Fallback to localStorage for offline/error cases
    const today = new Date().toISOString().split('T')[0];
    const stockKey = `vip-stock-${this.product.id || 'default'}-${today}`;
    
    try {
      const storedStock = localStorage.getItem(stockKey);
      if (storedStock) {
        return parseInt(storedStock);
      }
    } catch (e) {
      console.warn('Could not read stock from localStorage:', e);
    }
    
    // Return initial stock from metafields
    const initialStock = this.config.quantityTotal || 5;
    this.setCurrentStock(initialStock, today);
    return initialStock;
  }

  /**
   * Synchronous version of getCurrentStock for use in calculateWindowStatus
   */
  getCurrentStockSync() {
    const poolSold = parseInt(this.element.dataset.poolSold || '0');
    const poolTotal = parseInt(this.element.dataset.poolTotal || '5');
    const remaining = Math.max(0, poolTotal - poolSold);
    
    // Store global pool total for use in renderAvailability
    this.globalPoolTotal = poolTotal;
    
    return remaining;
  }
  
  /**
   * Set current VIP stock for today
   */
  setCurrentStock(newStock, date) {
    const today = date || new Date().toISOString().split('T')[0];
    const stockKey = `vip-stock-${this.product.id || 'default'}-${today}`;
    
    try {
      localStorage.setItem(stockKey, newStock.toString());
      debugLog(`VIP stock updated: ${newStock} for ${today}`);
    } catch (e) {
      console.warn('Could not save stock to localStorage:', e);
    }
  }
  
  /**
   * Decrement VIP stock (called when someone adds VIP to cart)
   * Note: This is now handled by the webhook, but kept for compatibility
   */
  async decrementStock() {
    const currentStock = await this.getCurrentStock();
    if (currentStock > 0) {
      // The webhook will handle the actual decrement
      // This is just for UI updates
      debugLog(`VIP stock decremented locally: ${currentStock} → ${currentStock - 1}`);
      this.render(); // Update display immediately
      return true;
    } else {
      debugLog('Cannot decrement VIP stock: already at 0');
      return false;
    }
  }

  init() {
    debugLog('VipTimer init called with product:', this.product);
    debugLog('Metafields:', this.product?.metafields?.custom);
    debugLog('Timer type from element:', this.element.dataset.timerType);
    
    // Determine timer type
    this.timerType = this.element.dataset.timerType || 'vip-daily';
    
    // Check if this is a timer-enabled product
    const isVipDaily = this.product?.metafields?.custom?.enabled;
    const isEventBased = ['vip-full-moon', 'vip-new-moon', 'vip-special-event', 'vip-tarot-daily'].includes(this.timerType);
    
    if (!isVipDaily && !isEventBased) {
      debugLog('No timer enabled for this product');
      return; // Not a timer product
    }
    
    debugLog(`Timer enabled (${this.timerType}), proceeding with initialization`);

    // Read shop-level overrides from data attributes
    const dataStart = (this.element.dataset.startTime || '').trim();
    const dataEnd = (this.element.dataset.endTime || '').trim();
    const blockedNights = (this.element.dataset.blockedNights || '').trim();

    const productStart = this.product.metafields.custom.start_time || '';
    const productEnd = this.product.metafields.custom.end_time || '';

    // Precedence: shop-level overrides > product-level > defaults
    const start = dataStart || productStart || '21:00';
    const end = dataEnd || productEnd || '05:00';

    this.blockedNightSet = new Set();
    if (blockedNights) {
      blockedNights.split(',').map(s => s.trim().toLowerCase()).forEach(d => {
        if (d) this.blockedNightSet.add(d);
      });
    }

    this.config = {
      enabled: this.product.metafields.custom.enabled || isEventBased,
      startTime: start,
      endTime: end,
      quantityTotal: this.product.metafields.custom.quantity_total || 0,
      timerType: this.timerType,
      eventDate: this.element.dataset.eventDate || null
    };

    debugLog('VIP config:', this.config);
    debugLog('About to render timer...');
    
    // Initialize asynchronously to fetch global pool data
    this.initAsync();
    
    // Store reference for external access
    this.element._vipTimerInstance = this;
    
    // Store bound event handlers for proper cleanup in destroy()
    this.boundDecrementHandler = () => this.decrementStock();
    this.boundVariantChangeHandler = () => this.scheduleVipButtonUpdate();
    this.boundInputChangeHandler = (e) => {
      if (e.target.matches('input[name="id"], input[type="radio"]')) {
        this.scheduleVipButtonUpdate();
      }
    };
    
    // Listen for decrement events
    document.addEventListener('vip:decrement-stock', this.boundDecrementHandler);
    
    // Debounced VIP button state update to handle multiple rapid events
    this.vipUpdateTimeout = null;
    this.scheduleVipButtonUpdate = () => {
      if (this.vipUpdateTimeout) {
        clearTimeout(this.vipUpdateTimeout);
      }
      this.vipUpdateTimeout = setTimeout(() => {
        this.forceUpdateVipButtonStates();
      }, 200);
    };
    
    // Listen for variant changes to re-apply VIP button states
    document.addEventListener('variant:change', this.boundVariantChangeHandler);
    
    // Also listen for other possible events that might trigger variant changes
    document.addEventListener('change', this.boundInputChangeHandler);
  }

  /**
   * Initialize asynchronously to fetch global pool data
   */
  async initAsync() {
    try {
      // Fetch current stock from global pool
      await this.getCurrentStock();
      
      this.render();
      this.updateVipButtonStates();
      this.startCountdown();
      this.setupVisibilityObserver();
    } catch (error) {
      console.error('Error initializing VIP timer:', error);
      // Fallback to synchronous initialization
      this.render();
      this.updateVipButtonStates();
      this.startCountdown();
      this.setupVisibilityObserver();
    }
  }

  /**
   * Calculate VIP window status
   */
  calculateWindowStatus() {
    // TEST MODE: Override for weekend testing
    const testMode = localStorage.getItem('vip-test-mode') === 'true';
    if (testMode) {
      debugLog('VIP Timer: TEST MODE ENABLED - Simulating VIP availability');
      return { 
        status: 'open',
        message: 'VIP ordering now open (TEST MODE)',
        timeRemaining: 2 * 60 * 60 * 1000 // 2 hours remaining
      };
    }
    
    const now = new Date();
    const storeTime = new Date(now.toLocaleString("en-US", {timeZone: this.storeTimezone}));
    
    // Handle event-based timers (monthly/special events)
    if (this.config.timerType === 'vip-full-moon' || 
        this.config.timerType === 'vip-new-moon' || 
        this.config.timerType === 'vip-special-event') {
      return this.calculateEventStatus(storeTime);
    }
    
    // Handle VIP Tarot Daily (same logic as VIP Daily)
    if (this.config.timerType === 'vip-tarot-daily') {
      // Use the same daily window logic as VIP - continue to regular daily logic below
    }
    
    // Parse start and end times with validation
    let startHour = 21, startMin = 0; // Defaults
    let endHour = 5, endMin = 0; // Defaults
    
    // Safely parse start time
    if (this.config.startTime && typeof this.config.startTime === 'string' && this.config.startTime.includes(':')) {
      const startParts = this.config.startTime.split(':').map(Number);
      if (!isNaN(startParts[0])) startHour = startParts[0];
      if (!isNaN(startParts[1])) startMin = startParts[1];
    }
    
    // Safely parse end time
    if (this.config.endTime && typeof this.config.endTime === 'string') {
      if (this.config.endTime.includes(':')) {
        const endParts = this.config.endTime.split(':').map(Number);
        if (!isNaN(endParts[0])) endHour = endParts[0];
        if (!isNaN(endParts[1])) endMin = endParts[1];
      } else {
        // Handle format like "10" -> 10:00 AM next day
        const parsed = parseInt(this.config.endTime);
        if (!isNaN(parsed)) endHour = parsed;
      }
    }
    
    debugLog('Parsed times:', { startHour, startMin, endHour, endMin });
    
    // Build window start/end for today first
    let startTime = new Date(storeTime);
    startTime.setHours(startHour, startMin, 0, 0);
    
    let endTime = new Date(storeTime);
    endTime.setHours(endHour, endMin, 0, 0);
    
    // Robust overnight detection: end on or before start means it crosses midnight
    const isOvernight = endHour <= startHour;
    if (isOvernight) {
      // If current time is before the end hour, we might be in yesterday's window
      if (storeTime.getHours() < endHour || (storeTime.getHours() === endHour && storeTime.getMinutes() < endMin)) {
        // We're in the morning part of the window that started yesterday
        startTime.setDate(startTime.getDate() - 1);
        // endTime stays as today (the end of yesterday's window)
        endTime.setDate(storeTime.getDate());
      } else {
        // We're in the evening, waiting for today's window to start
        // startTime is today, endTime is tomorrow
        endTime.setDate(endTime.getDate() + 1);
      }
    }
    
    // Check if it's a weekend (Friday 21:00-Saturday 05:00 or Saturday 21:00-Sunday 05:00)
    const dayOfWeek = storeTime.getDay(); // 0=Sunday, 1=Monday, ..., 6=Saturday
    const currentHour = storeTime.getHours();
    const isWeekendBlocked = this.isWeekendBlocked(dayOfWeek, currentHour);
    
    if (isWeekendBlocked) {
      const nextAvailable = this.getNextAvailableTime(storeTime, dayOfWeek);
      return {
        status: 'weekend_blocked',
        message: 'VIP ordering unavailable on weekends',
        nextAvailable: nextAvailable,
        timeRemaining: nextAvailable - storeTime  // ✅ FIX: Add timeRemaining for countdown updates
      };
    }
    
    // Check current window status
    if (storeTime >= startTime && storeTime < endTime) {
      // Within today's window - check if sold out
      const currentStock = this.getCurrentStockSync();
      if (currentStock <= 0) {
        return {
          status: 'sold_out',
          message: 'VIP sold out for today',
          timeRemaining: endTime - storeTime,
          endTime: endTime
        };
      }
      
      return {
        status: 'open',
        message: 'VIP ordering now open',
        timeRemaining: endTime - storeTime,
        endTime: endTime
      };
    } else if (storeTime < startTime) {
      // Before today's window opens - use smart countdown to next available opening
      const nextAvailableOpening = this.findNextAvailableOpening(storeTime);
      return {
        status: 'closed_opens_later',
        message: 'VIP ordering opens soon',
        timeRemaining: nextAvailableOpening.timeRemaining,
        startTime: nextAvailableOpening.startTime,
        actualStartTime: nextAvailableOpening.actualStartTime || nextAvailableOpening.startTime
      };
    } else {
      // After today's window closed, opens tomorrow
      const nextStart = new Date(startTime);
      nextStart.setDate(nextStart.getDate() + 1);
      return {
        status: 'closed_opens_tomorrow',
        message: 'VIP ordering opens tomorrow',
        timeRemaining: nextStart - storeTime,
        startTime: nextStart
      };
    }
  }

  /**
   * Find the next available opening, checking up to 6 days ahead
   */
  findNextAvailableOpening(currentTime) {
    debugLog('Finding next available opening from:', currentTime.toLocaleString());
    
    // Safely parse start time with defaults
    let startHour = 21, startMin = 0;
    if (this.config.startTime && typeof this.config.startTime === 'string' && this.config.startTime.includes(':')) {
      const parts = this.config.startTime.split(':').map(Number);
      if (!isNaN(parts[0])) startHour = parts[0];
      if (!isNaN(parts[1])) startMin = parts[1];
    }
    
    // Check up to 6 days ahead to find next available opening
    for (let dayOffset = 0; dayOffset < 6; dayOffset++) {
      const checkDate = new Date(currentTime.getTime() + (dayOffset * 24 * 60 * 60 * 1000));
      const dayOfWeek = checkDate.getDay();
      
      debugLog(`Checking day ${dayOffset}: ${checkDate.toLocaleDateString()} (${this.getDayName(dayOfWeek)})`);
      
      // Skip if this day is blocked
      if (this.isThisDayBlocked(dayOfWeek)) {
        debugLog(`  -> ${this.getDayName(dayOfWeek)} is blocked, skipping`);
        continue;
      }
      
      // This day is available! Calculate the time until this day's opening
      const nextOpeningTime = new Date(checkDate);
      nextOpeningTime.setHours(startHour, startMin, 0, 0);
      
      debugLog(`  -> ${this.getDayName(dayOfWeek)} is available! Opening at: ${nextOpeningTime.toLocaleString()}`);
      
      const timeRemaining = nextOpeningTime - currentTime;
      
      return {
        timeRemaining: timeRemaining,
        startTime: nextOpeningTime,
        actualStartTime: nextOpeningTime,
        dayOffset: dayOffset,
        availableDay: this.getDayName(dayOfWeek)
      };
    }
    
    // Fallback if somehow no day is found (shouldn't happen with 6 days)
    console.warn('No available opening found in next 6 days - fallback to tomorrow');
    const fallbackTime = new Date(currentTime);
    fallbackTime.setDate(fallbackTime.getDate() + 1);
    fallbackTime.setHours(startHour, startMin, 0, 0);
    
    return {
      timeRemaining: fallbackTime - currentTime,
      startTime: fallbackTime,
      actualStartTime: fallbackTime,
      dayOffset: 1,
      availableDay: 'unknown'
    };
  }

  /**
   * Check if a specific day of the week is blocked
   */
  isThisDayBlocked(dayOfWeek) {
    const dayNames = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
    const dayName = dayNames[dayOfWeek];
    
    return this.blockedNightSet && this.blockedNightSet.has(dayName);
  }

  /**
   * Get day name from day number
   */
  getDayName(dayOfWeek) {
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return dayNames[dayOfWeek];
  }

  /**
   * Get friendly name for event type
   */
  getEventTypeName() {
    switch (this.config.timerType) {
      case 'vip-full-moon': return 'Full Moon';
      case 'vip-new-moon': return 'New Moon';
      case 'vip-special-event': return 'Special Event';
      default: return 'Event';
    }
  }

  /**
   * Get moon casting label for monthly events
   */
  getMoonCastingLabel() {
    switch (this.config.timerType) {
      case 'vip-full-moon':
        return 'Full Moon Casting';
      case 'vip-new-moon':
        return 'New Moon Casting';
      case 'vip-special-event':
        return 'Special Event';
      default:
        return 'Moon Casting';
    }
  }

  /**
   * Calculate Event-based timer status (Full Moon, New Moon, Special Events)
   */
  calculateEventStatus(storeTime) {
    if (!this.config.eventDate) {
      const eventTypeName = this.getEventTypeName();
      return {
        status: 'no_event',
        message: `No ${eventTypeName} event scheduled`,
        timeRemaining: 0
      };
    }

    try {
      // Parse the event date (format: "2025-01-13" or "Jan 13, 2025")
      const eventDate = new Date(this.config.eventDate);
      if (isNaN(eventDate.getTime())) {
        throw new Error(`Invalid event date: ${this.config.eventDate}`);
      }

      // Set event end time to end of day (23:59:59)
      const eventEndTime = new Date(eventDate);
      eventEndTime.setHours(23, 59, 59, 999);

      // Check if event has passed
      if (storeTime > eventEndTime) {
        const eventTypeName = this.getEventTypeName();
        return {
          status: 'event_ended',
          message: `${eventTypeName} event has ended`,
          timeRemaining: 0
        };
      }

      // Check if event is today (event day)
      const today = new Date(storeTime);
      today.setHours(0, 0, 0, 0);
      const eventDay = new Date(eventDate);
      eventDay.setHours(0, 0, 0, 0);

      if (today.getTime() === eventDay.getTime()) {
        // Event is today - check stock
        const currentStock = this.getCurrentStockSync();
        const eventTypeName = this.getEventTypeName();
        
        if (currentStock <= 0) {
          return {
            status: 'sold_out',
            message: `${eventTypeName} slots sold out`,
            timeRemaining: eventEndTime - storeTime,
            endTime: eventEndTime
          };
        }

        return {
          status: 'event_day',
          message: `${eventTypeName} is today!`,
          timeRemaining: eventEndTime - storeTime,
          endTime: eventEndTime
        };
      }

      // Event is in the future - customers can order in advance
      if (storeTime < eventDay) {
        const currentStock = this.getCurrentStockSync();
        const eventTypeName = this.getEventTypeName();
        
        if (currentStock <= 0) {
          return {
            status: 'sold_out',
            message: `${eventTypeName} slots sold out`,
            timeRemaining: eventDay - storeTime,
            endTime: eventDay
          };
        }

        return {
          status: 'open',
          message: `${eventTypeName} orders open`,
          timeRemaining: eventDay - storeTime,
          eventDate: eventDay
        };
      }

    } catch (error) {
      console.error('Error calculating Full Moon status:', error);
      return {
        status: 'error',
        message: 'Error calculating event status',
        timeRemaining: 0
      };
    }
  }

  /**
   * Check if current time falls within weekend blocked periods
   */
  isWeekendBlocked(dayOfWeek, currentHour) {
    // Support shop-level blocked nights (fri,sat,etc.). If set, use it instead of hard-coded rules.
    if (this.blockedNightSet && this.blockedNightSet.size > 0) {
      const map = ['sun','mon','tue','wed','thu','fri','sat'];
      const today = map[dayOfWeek];
      // If the current time is in the evening window for a blocked night, or early morning of the following day.
      const startParts = (this.config.startTime || '21:00').split(':').map(Number);
      const endParts = (this.config.endTime || '05:00').split(':').map(Number);
      const startHour = startParts[0] || 21;
      const endHour = endParts[0] || 5;
      const isOvernight = startHour >= 12 && endHour < 12; // generic overnight check

      if (this.blockedNightSet.has(today)) {
        if (isOvernight) {
          // evening of today qualifies as blocked
          if (currentHour >= startHour) return true;
        } else {
          if (currentHour >= startHour && currentHour < endHour) return true;
        }
      }
      // Early morning: if yesterday was blocked and it is overnight, block until endHour
      const yesterday = map[(dayOfWeek + 6) % 7];
      if (isOvernight && this.blockedNightSet.has(yesterday)) {
        if (currentHour < endHour) return true;
      }
      return false;
    }

    // Default hard-coded weekend rules
    if (dayOfWeek === 5 && currentHour >= 21) return true; // Friday evening
    if (dayOfWeek === 6 && currentHour < 5) return true;   // Saturday early morning
    if (dayOfWeek === 6 && currentHour >= 21) return true; // Saturday evening
    if (dayOfWeek === 0 && currentHour < 5) return true;   // Sunday early morning
    return false;
  }

  /**
   * Get next available time after weekend
   */
  getNextAvailableTime(currentTime, dayOfWeek) {
    const nextStart = new Date(currentTime);
    
    if (dayOfWeek === 5 || dayOfWeek === 6) {
      // Friday or Saturday - next available is Sunday at start time
      const daysUntilSunday = dayOfWeek === 5 ? 2 : 1;
      nextStart.setDate(nextStart.getDate() + daysUntilSunday);
    } else if (dayOfWeek === 0) {
      // Sunday - if before end time, next available is Monday
      nextStart.setDate(nextStart.getDate() + 1);
    }
    
    // Safely parse start time with defaults
    let startHour = 21, startMin = 0;
    if (this.config.startTime && typeof this.config.startTime === 'string' && this.config.startTime.includes(':')) {
      const parts = this.config.startTime.split(':').map(Number);
      if (!isNaN(parts[0])) startHour = parts[0];
      if (!isNaN(parts[1])) startMin = parts[1];
    }
    nextStart.setHours(startHour, startMin, 0, 0);
    
    return nextStart;
  }

  /**
   * Format time remaining as human-readable string
   */
  formatTimeRemaining(milliseconds) {
    // ✅ FIX: Handle NaN/invalid values gracefully
    if (isNaN(milliseconds) || milliseconds === null || milliseconds === undefined) {
      debugLog('formatTimeRemaining received invalid value:', milliseconds);
      return '--';
    }
    
    const totalSeconds = Math.floor(milliseconds / 1000);
    
    // Handle negative or zero values
    if (totalSeconds <= 0) {
      return '0s';
    }
    
    const days = Math.floor(totalSeconds / (24 * 3600));
    const hours = Math.floor((totalSeconds % (24 * 3600)) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    // For long countdowns (1+ days), show days and hours
    if (days > 0) {
      return `${days}d ${hours}h`;
    } else if (hours > 0) {
      // ✅ FIX: Always show hours AND minutes for consistency
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  }

  /**
   * Render only the timer display (without affecting VIP button states)
   */
  async renderTimerOnly() {
    // debugLog('VipTimer renderTimerOnly called');
    const status = this.calculateWindowStatus();
    debugLog('Window status:', status);
    
    // Only update the timer content, not the entire component
    const timerContent = this.element.querySelector('.vip-timer__content');
    if (!timerContent) {
      // If no timer content exists, do a full render
      return this.render();
    }
    
    const isCart = this.isCart;
    const availabilityData = await this.renderAvailability();
    const availability = availabilityData.html;
    
    // Update only the timer cells, preserving the overall structure
    // ✅ FIX: Check for both --timer and --status cells (different states use different structures)
    const timerCell = timerContent.querySelector('.vip-timer__cell--timer') || 
                      timerContent.querySelector('.vip-timer__cell--status');
    const availabilityCell = timerContent.querySelector('.vip-timer__cell--availability');
    
    // Update countdown value if timer cell exists
    if (timerCell) {
      const timeRemaining = this.formatTimeRemaining(status.timeRemaining);
      const timerValue = timerCell.querySelector('.vip-timer__value');
      if (timerValue) {
        timerValue.textContent = timeRemaining;
        debugLog('Timer value updated:', timeRemaining);
      }
    }
    
    // Update availability (stock count) if availability cell exists
    if (availabilityCell) {
      const isSoldOut = availabilityData.value === 'Sold Out';
      
      if (isSoldOut) {
        // ✅ FIX: Show clean "Sold Out" text for ALL timer types when sold out
        availabilityCell.innerHTML = `
          <span class="vip-timer__label">VIP</span>
          <span class="vip-timer__value">Sold Out</span>
        `;
      } else {
        // Normal availability update for available stock
        availabilityCell.innerHTML = `
          <span class="vip-timer__label">${availabilityData.label}</span>
          <span class="vip-timer__value">${availabilityData.value.replace(' left', '').replace(' available', '')} ${availabilityData.value.includes('left') ? 'Left' : 'Available'}</span>
        `;
      }
    }
    
    // ✅ FIX: If neither cell found, fall back to full render (handles edge cases)
    if (!timerCell && !availabilityCell) {
      debugLog('Timer cells not found, falling back to full render');
      return this.render();
    }
  }

  /**
   * Render the timer component
   */
  async render() {
    debugLog('VipTimer render called');
    const status = this.calculateWindowStatus();
    debugLog('Window status:', status);

    let html = '';
    const isCart = this.isCart;
    const isCartItem = this.element.classList.contains('vip-timer--cart-item');
    const availabilityData = await this.renderAvailability();
    const availability = availabilityData.html;
    
    // Use compact layout for cart item timers
    if (isCartItem) {
      return this.renderCartItemTimer(status);
    }
    
    switch (status.status) {
      case 'open':
        if (isCart) {
          html = `
            <div class="vip-timer vip-timer--open">
              <div class="vip-timer__status">
                <span class="vip-timer__icon">🟢</span>
                <span class="vip-timer__message">VIP Items Available</span>
              </div>
              <div class="vip-timer__countdown">
                <span class="vip-timer__time">${this.formatTimeRemaining(status.timeRemaining)}</span>
                <span class="vip-timer__label">to checkout</span>
              </div>
              ${availability}
            </div>
          `;
        } else {
          const availabilityInfo = availabilityData;
          const isEventBased = ['vip-full-moon', 'vip-new-moon', 'vip-special-event'].includes(this.config.timerType);
          
          if (isEventBased) {
            // Monthly event timer - uses same classes as daily VIP open for consistent styling
            html = `
            <div class="vip-timer vip-timer--open vip-timer--product-open vip-timer--moon-open">
              <div class="vip-timer__content">
                <div class="vip-timer__cell vip-timer__cell--status">
                  <span class="vip-timer__label">VIP</span>
                  <span class="vip-timer__value">is open</span>
                </div>
                <div class="vip-timer__cell vip-timer__cell--timer">
                  <span class="vip-timer__label">Closes in</span>
                  <span class="vip-timer__value">${this.formatTimeRemaining(status.timeRemaining)}</span>
                </div>
                <div class="vip-timer__cell vip-timer__cell--availability">
                  <span class="vip-timer__label">Only</span>
                  <span class="vip-timer__value">${availabilityInfo.value.replace(' left', '').replace(' available', '')} Left</span>
                </div>
              </div>
            </div>
          `;
          } else {
            // Daily timer - keep existing layout
            html = `
            <div class="vip-timer vip-timer--open vip-timer--product-open">
              <div class="vip-timer__content">
                <div class="vip-timer__cell vip-timer__cell--status">
                  <span class="vip-timer__label">VIP</span>
                  <span class="vip-timer__value">is open</span>
                </div>
                <div class="vip-timer__cell vip-timer__cell--timer">
                  <span class="vip-timer__label">Closes in</span>
                  <span class="vip-timer__value">${this.formatTimeRemaining(status.timeRemaining)}</span>
                </div>
                <div class="vip-timer__cell vip-timer__cell--availability">
                  <span class="vip-timer__label">Only</span>
                  <span class="vip-timer__value">${availabilityInfo.value.replace(' left', '').replace(' available', '')} ${availabilityInfo.value.includes('left') ? 'Left' : 'Available'}</span>
                </div>
              </div>
            </div>
          `;
          }
        }
        break;
        
      case 'closed_opens_later':
      case 'closed_opens_tomorrow':
        if (isCart) {
          // Cart: Keep existing cart-specific layout
          html = `
            <div class="vip-timer vip-timer--closed">
              <div class="vip-timer__status">
                <span class="vip-timer__icon">⏰</span>
                <span class="vip-timer__message">VIP Items Unavailable</span>
              </div>
              <div class="vip-timer__countdown">
                <span class="vip-timer__time">${this.formatTimeRemaining(status.timeRemaining)}</span>
                <span class="vip-timer__label">until available</span>
              </div>
              ${availability}
            </div>
          `;
        } else {
          const availabilityInfo = availabilityData;
          html = `
            <div class="vip-timer vip-timer--closed vip-timer--product-unavailable">
              <div class="vip-timer__content">
                <div class="vip-timer__cell vip-timer__cell--status">
                  <span class="vip-timer__label">VIP opens in</span>
                  <span class="vip-timer__value">${this.formatTimeRemaining(status.timeRemaining)}</span>
                </div>
                <div class="vip-timer__cell vip-timer__cell--notify">
                  <button class="vip-timer__notify-btn" data-vip-notify>Notify Me</button>
                  <span class="vip-timer__value">when available</span>
                </div>
                <div class="vip-timer__cell vip-timer__cell--availability">
                  <span class="vip-timer__label">Only</span>
                  <span class="vip-timer__value">${availabilityInfo.value}</span>
                </div>
              </div>
            </div>
          `;
        }
        break;
        
      case 'weekend_blocked':
        if (isCart) {
          html = `
            <div class="vip-timer vip-timer--weekend">
              <div class="vip-timer__status">
                <span class="vip-timer__icon">🚫</span>
                <span class="vip-timer__message">VIP Items Unavailable Weekends</span>
              </div>
              <div class="vip-timer__countdown">
                <span class="vip-timer__time">${this.formatTimeRemaining(status.nextAvailable - new Date())}</span>
                <span class="vip-timer__label">until next available</span>
              </div>
              ${availability}
            </div>
          `;
        } else {
          const timeUntil = this.formatTimeRemaining(status.nextAvailable - new Date());
          const availabilityInfo = availabilityData;
          html = `
            <div class="vip-timer vip-timer--weekend vip-timer--product-unavailable">
              <div class="vip-timer__content">
                <div class="vip-timer__cell vip-timer__cell--status">
                  <span class="vip-timer__label">VIP opens in</span>
                  <span class="vip-timer__value">${timeUntil}</span>
                </div>
                <div class="vip-timer__cell vip-timer__cell--notify">
                  <button class="vip-timer__notify-btn" data-vip-notify>Notify Me</button>
                  <span class="vip-timer__value">when available</span>
                </div>
                <div class="vip-timer__cell vip-timer__cell--availability">
                  <span class="vip-timer__label">Only</span>
                  <span class="vip-timer__value">${availabilityInfo.value}</span>
                </div>
              </div>
            </div>
          `;
        }
        break;
        
      case 'sold_out':
        if (isCart) {
          html = `
            <div class="vip-timer vip-timer--sold-out">
              <div class="vip-timer__status">
                <span class="vip-timer__icon">🔴</span>
                <span class="vip-timer__message">VIP Items Sold Out</span>
              </div>
              <div class="vip-timer__countdown">
                <span class="vip-timer__time">${this.formatTimeRemaining(status.timeRemaining)}</span>
                <span class="vip-timer__label">until next window</span>
              </div>
              <div class="vip-timer__availability">0 available</div>
            </div>
          `;
        } else {
          const isEventBased = ['vip-full-moon', 'vip-new-moon', 'vip-special-event'].includes(this.config.timerType);
          
          if (isEventBased) {
            // Monthly event timer - uses same classes as daily sold out for consistent styling
            html = `
            <div class="vip-timer vip-timer--sold-out vip-timer--product-sold-out vip-timer--moon-sold-out">
              <div class="vip-timer__content">
                <div class="vip-timer__cell vip-timer__cell--status">
                  <span class="vip-timer__label">Casting in</span>
                  <span class="vip-timer__value">${this.formatTimeRemaining(status.timeRemaining)}</span>
                </div>
                <div class="vip-timer__cell vip-timer__cell--notify">
                  <button class="vip-timer__notify-btn" data-vip-notify>Notify Me</button>
                  <span class="vip-timer__value">next moon</span>
                </div>
                <div class="vip-timer__cell vip-timer__cell--availability">
                  <span class="vip-timer__label">VIP</span>
                  <span class="vip-timer__value">Sold Out</span>
                </div>
              </div>
            </div>
          `;
          } else {
            // Daily timer - keep existing layout
            html = `
            <div class="vip-timer vip-timer--sold-out vip-timer--product-sold-out">
              <div class="vip-timer__content">
                <div class="vip-timer__cell vip-timer__cell--status">
                  <span class="vip-timer__label">VIP sold out</span>
                  <span class="vip-timer__value">${this.formatTimeRemaining(status.timeRemaining)}</span>
                </div>
                <div class="vip-timer__cell vip-timer__cell--notify">
                  <button class="vip-timer__notify-btn" data-vip-notify>Notify Me</button>
                  <span class="vip-timer__value">when available</span>
                </div>
                <div class="vip-timer__cell vip-timer__cell--availability">
                  <span class="vip-timer__label">VIP</span>
                  <span class="vip-timer__value">Sold Out</span>
                </div>
              </div>
            </div>
          `;
          }
        }
        break;
        
      // Event-based timer statuses
      case 'event_day':
        if (isCart) {
          html = `
            <div class="vip-timer vip-timer--open">
              <div class="vip-timer__status">
                <span class="vip-timer__icon">🌟</span>
                <span class="vip-timer__message">${this.getEventTypeName()} Event Day!</span>
              </div>
              <div class="vip-timer__countdown">
                <span class="vip-timer__time">${this.formatTimeRemaining(status.timeRemaining)}</span>
                <span class="vip-timer__label">until event</span>
              </div>
              ${availability}
            </div>
          `;
        } else {
          const availabilityInfo = availabilityData;
          const eventTypeName = this.getEventTypeName();
          // ✅ Event day is only for moon/special events, so always add moon class for consistent styling
          const isEventBased = ['vip-full-moon', 'vip-new-moon', 'vip-special-event'].includes(this.config.timerType);
          const moonClass = isEventBased ? ' vip-timer--moon-open' : '';
          html = `
            <div class="vip-timer vip-timer--open vip-timer--product-open${moonClass}">
              <div class="vip-timer__content">
                <div class="vip-timer__cell vip-timer__cell--status">
                  <span class="vip-timer__label">${eventTypeName}</span>
                  <span class="vip-timer__value">is TODAY!</span>
                </div>
                <div class="vip-timer__cell vip-timer__cell--timer">
                  <span class="vip-timer__label">Event in</span>
                  <span class="vip-timer__value">${this.formatTimeRemaining(status.timeRemaining)}</span>
                </div>
                <div class="vip-timer__cell vip-timer__cell--availability">
                  <span class="vip-timer__label">Only</span>
                  <span class="vip-timer__value">${availabilityInfo.value.replace(' left', '').replace(' available', '')} ${availabilityInfo.value.includes('left') ? 'Left' : 'Available'}</span>
                </div>
              </div>
            </div>
          `;
        }
        break;
        
      case 'upcoming':
        // This case is now removed - events before the date show as 'open'
        break;
        
      case 'event_ended':
        if (isCart) {
          html = `
            <div class="vip-timer vip-timer--weekend">
              <div class="vip-timer__status">
                <span class="vip-timer__icon">🌑</span>
                <span class="vip-timer__message">Full Moon Event Ended</span>
              </div>
              <div class="vip-timer__availability">Event completed</div>
            </div>
          `;
        } else {
          const eventTypeName = this.getEventTypeName();
          html = `
            <div class="vip-timer vip-timer--weekend vip-timer--product-unavailable">
              <div class="vip-timer__content">
                <div class="vip-timer__cell vip-timer__cell--status">
                  <span class="vip-timer__label">${eventTypeName}</span>
                  <span class="vip-timer__value">Event Ended</span>
                </div>
                <div class="vip-timer__cell vip-timer__cell--notify">
                  <span class="vip-timer__value">Next event</span>
                  <span class="vip-timer__value">coming soon</span>
                </div>
                <div class="vip-timer__cell vip-timer__cell--availability">
                  <span class="vip-timer__label">Event</span>
                  <span class="vip-timer__value">Completed</span>
                </div>
              </div>
            </div>
          `;
        }
        break;
        
      case 'no_event':
      case 'error':
        html = `
          <div class="vip-timer vip-timer--weekend">
            <div class="vip-timer__status">
              <span class="vip-timer__icon">⚠️</span>
              <span class="vip-timer__message">${status.message}</span>
            </div>
          </div>
        `;
        break;
    }
    
    debugLog('Setting innerHTML:', html);
    this.element.innerHTML = html;
    debugLog('Timer rendered successfully');
    
    // Set up notify button if on product page
    if (!isCart) {
      this.setupNotifyButton();
    }
  }
  
  /**
   * Render compact cart item timer
   */
  async renderCartItemTimer(status) {
    // ✅ Set data attributes for reliable status detection (used by cart page)
    const isAvailable = (status.status === 'open' || status.status === 'event_day');
    this.element.setAttribute('data-vip-status', status.status);
    this.element.setAttribute('data-vip-available', isAvailable ? 'true' : 'false');
    
    const timerHtml = this.formatCartTimer(status);
    
    // ✅ Only show Notify button when VIP is UNAVAILABLE
    const notifyButton = isAvailable ? '' : `
        <button class="cart-vip-status__notify" data-vip-notify>
          ${this.getNotifyLabel(status)}
        </button>
    `;
    
    const html = `
      <div class="cart-vip-status">
        <span class="cart-vip-status__label">VIP ${this.getStatusLabel(status)}</span>
        ${timerHtml}
        ${notifyButton}
      </div>
    `;
    
    this.element.innerHTML = html;
    
    // Set up notify button for cart item (only if button exists)
    if (!isAvailable) {
      this.setupNotifyButton();
    }
    
    // ✅ Emit status change event (useful for cart page status updates)
    this.element.dispatchEvent(new CustomEvent('vip:status-change', {
      bubbles: true,
      detail: { 
        status: status.status, 
        available: isAvailable,
        timerType: this.config.timerType
      }
    }));
  }
  
  /**
   * Get status label for cart timers
   */
  getStatusLabel(status) {
    switch (status.status) {
      case 'open': return 'Available';
      case 'event_day': return 'Available';
      case 'sold_out': return 'Sold Out';
      case 'event_ended': return 'Sold Out';
      default: return 'Unavailable';
    }
  }
  
  /**
   * Get notify button label for cart timers
   */
  getNotifyLabel(status) {
    switch (this.config.timerType) {
      case 'vip-daily': 
      case 'vip-tarot-daily': return 'Notify Me';
      case 'vip-full-moon': 
      case 'vip-new-moon': return 'Notify Me on Next Moon ';
      case 'vip-special-event': return 'Subscribe for Next Event';
      default: return 'Notify Me';
    }
  }
  
  /**
   * Format timer for compact cart display
   */
  formatCartTimer(status) {
    if (status.status === 'open' || status.status === 'event_day') {
      return `<span class="cart-vip-status__timer">• Closes in ${this.formatTimeRemaining(status.timeRemaining)}</span>`;
    } else if (status.status === 'closed_opens_later' || status.status === 'closed_opens_tomorrow') {
      return `<span class="cart-vip-status__timer">• Opens in ${this.formatTimeRemaining(status.timeRemaining)}</span>`;
    } else if (status.status === 'weekend_blocked' && status.nextAvailable) {
      // For weekend blocked, show when it opens next
      const timeUntilNext = status.nextAvailable - new Date();
      return `<span class="cart-vip-status__timer">• Opens in ${this.formatTimeRemaining(timeUntilNext)}</span>`;
    } else if (status.status === 'sold_out') {
      // ✅ For sold out, show when the next window opens
      // For event-based timers (moon, special), don't show timer (no daily windows)
      if (['vip-full-moon', 'vip-new-moon', 'vip-special-event'].includes(this.config.timerType)) {
        return '';
      }
      
      // Calculate time until next window opens (tomorrow at start time)
      const timeUntilNext = this.getTimeUntilNextDailyWindow();
      if (timeUntilNext > 0) {
        return `<span class="cart-vip-status__timer">• Opens in ${this.formatTimeRemaining(timeUntilNext)}</span>`;
      }
      return '';
    } else {
      return '';
    }
  }
  
  /**
   * Calculate milliseconds until the next daily VIP window opens
   * Used for sold out display - shows when tomorrow's window starts
   */
  getTimeUntilNextDailyWindow() {
    // Safely parse start time with defaults
    let startHour = 21, startMin = 0;
    if (this.config.startTime && typeof this.config.startTime === 'string' && this.config.startTime.includes(':')) {
      const parts = this.config.startTime.split(':').map(Number);
      if (!isNaN(parts[0])) startHour = parts[0];
      if (!isNaN(parts[1])) startMin = parts[1];
    }
    
    // Get current time in store timezone
    const now = new Date();
    const storeTimeStr = now.toLocaleString("en-US", {timeZone: this.storeTimezone});
    const storeTime = new Date(storeTimeStr);
    
    const currentHour = storeTime.getHours();
    const currentMin = storeTime.getMinutes();
    const currentSec = storeTime.getSeconds();
    
    // Calculate hours and minutes until tomorrow's start time
    // If we're in an overnight window (e.g., 9pm-5am) and it's sold out,
    // next opening is tomorrow at start time
    
    let hoursUntil, minutesUntil;
    
    if (currentHour >= startHour) {
      // We're past today's start time, so next is tomorrow
      // Hours until midnight + hours from midnight to start
      hoursUntil = (24 - currentHour) + startHour;
      minutesUntil = startMin - currentMin;
    } else {
      // We're before start time (morning of overnight window)
      // Next opening is tonight at start time
      hoursUntil = startHour - currentHour;
      minutesUntil = startMin - currentMin;
    }
    
    // Adjust for negative minutes
    if (minutesUntil < 0) {
      hoursUntil -= 1;
      minutesUntil += 60;
    }
    
    // Convert to milliseconds
    const msUntil = (hoursUntil * 60 * 60 * 1000) + 
                    (minutesUntil * 60 * 1000) - 
                    (currentSec * 1000);
    
    return msUntil > 0 ? msUntil : 0;
  }
  
  /**
   * Setup "Notify Me" button click handler
   */
  setupNotifyButton() {
    const notifyBtn = this.element.querySelector('[data-vip-notify]');
    if (notifyBtn) {
      notifyBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.showNotifyModal();
      });
    }
  }
  
  /**
   * Show notify me modal/form - Custom branded modal
   */
  showNotifyModal() {
    // Determine modal content based on timer type
    let title, subtitle, buttonText;
    
    if (this.config.timerType === 'vip-special-event') {
      title = 'Get Notified';
      subtitle = 'Enter your email to be notified about future special events';
      buttonText = 'Subscribe';
    } else if (['vip-full-moon', 'vip-new-moon'].includes(this.config.timerType)) {
      const timerTypeName = this.getEventTypeName();
      title = `${timerTypeName} Reminder`;
      subtitle = `Enter your email to be notified when the next ${timerTypeName} opens`;
      buttonText = 'Notify Me';
    } else {
      title = 'VIP Reminder';
      subtitle = 'Enter your email to be notified when VIP opens';
      buttonText = 'Notify Me';
    }
    
    // Create and show the custom modal
    this.createNotifyModal(title, subtitle, buttonText);
  }
  
  /**
   * Create the custom notify modal
   */
  createNotifyModal(title, subtitle, buttonText) {
    // Remove any existing modal
    const existingModal = document.getElementById('vip-notify-modal');
    if (existingModal) {
      existingModal.remove();
    }
    
    // Inject modal styles if not already present
    if (!document.getElementById('vip-notify-modal-styles')) {
      const styles = document.createElement('style');
      styles.id = 'vip-notify-modal-styles';
      styles.textContent = `
        .vip-notify-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 99999;
          opacity: 0;
          transition: opacity 0.2s ease;
        }
        .vip-notify-modal-overlay.active {
          opacity: 1;
        }
        .vip-notify-modal {
          background: #1a1a1a;
          border: 1px solid #333;
          border-radius: 12px;
          padding: 28px 32px;
          max-width: 400px;
          width: 90%;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
          transform: scale(0.9);
          transition: transform 0.2s ease;
        }
        .vip-notify-modal-overlay.active .vip-notify-modal {
          transform: scale(1);
        }
        .vip-notify-modal__header {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 8px;
        }
        .vip-notify-modal__icon {
          font-size: 24px;
        }
        .vip-notify-modal__title {
          font-size: 20px;
          font-weight: 600;
          color: #ffffff;
          margin: 0;
          font-family: inherit;
        }
        .vip-notify-modal__subtitle {
          color: rgba(255, 255, 255, 0.7);
          font-size: 14px;
          margin: 0 0 20px 0;
          line-height: 1.5;
        }
        .vip-notify-modal__input-wrapper {
          margin-bottom: 20px;
        }
        .vip-notify-modal__input {
          width: 100%;
          padding: 14px 16px;
          border: 1px solid #444;
          border-radius: 8px;
          background: #0d0d0d;
          color: #ffffff;
          font-size: 16px;
          outline: none;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
          box-sizing: border-box;
        }
        .vip-notify-modal__input:focus {
          border-color: #adbf55;
          box-shadow: 0 0 0 3px rgba(173, 191, 85, 0.2);
        }
        .vip-notify-modal__input::placeholder {
          color: rgba(255, 255, 255, 0.4);
        }
        .vip-notify-modal__input.error {
          border-color: #ff4757;
          box-shadow: 0 0 0 3px rgba(255, 71, 87, 0.2);
        }
        .vip-notify-modal__error {
          color: #ff4757;
          font-size: 13px;
          margin-top: 8px;
          display: none;
        }
        .vip-notify-modal__error.visible {
          display: block;
        }
        .vip-notify-modal__message {
          padding: 12px 16px;
          border-radius: 8px;
          font-size: 14px;
          margin-bottom: 16px;
          display: none;
        }
        .vip-notify-modal__message.success {
          display: block;
          background: rgba(173, 191, 85, 0.15);
          border: 1px solid rgba(173, 191, 85, 0.3);
          color: #adbf55;
        }
        .vip-notify-modal__message.error {
          display: block;
          background: rgba(255, 71, 87, 0.15);
          border: 1px solid rgba(255, 71, 87, 0.3);
          color: #ff4757;
        }
        .vip-notify-modal__buttons {
          display: flex;
          gap: 12px;
          justify-content: flex-end;
        }
        .vip-notify-modal__btn {
          padding: 12px 24px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          border: none;
          font-family: inherit;
        }
        .vip-notify-modal__btn--cancel {
          background: transparent;
          border: 1px solid #444;
          color: rgba(255, 255, 255, 0.7);
        }
        .vip-notify-modal__btn--cancel:hover {
          background: rgba(255, 255, 255, 0.05);
          border-color: #666;
        }
        .vip-notify-modal__btn--submit {
          background: #adbf55;
          color: #000000;
        }
        .vip-notify-modal__btn--submit:hover {
          background: #c4d66e;
        }
        .vip-notify-modal__btn--submit:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .vip-notify-modal__btn--submit.loading {
          position: relative;
          color: transparent;
        }
        .vip-notify-modal__btn--submit.loading::after {
          content: '';
          position: absolute;
          width: 16px;
          height: 16px;
          top: 50%;
          left: 50%;
          margin-left: -8px;
          margin-top: -8px;
          border: 2px solid #000;
          border-radius: 50%;
          border-top-color: transparent;
          animation: vip-notify-spin 0.8s linear infinite;
        }
        @keyframes vip-notify-spin {
          to { transform: rotate(360deg); }
        }
      `;
      document.head.appendChild(styles);
    }
    
    // Create modal HTML
    const modal = document.createElement('div');
    modal.id = 'vip-notify-modal';
    modal.className = 'vip-notify-modal-overlay';
    modal.innerHTML = `
      <div class="vip-notify-modal">
        <div class="vip-notify-modal__header">
          <span class="vip-notify-modal__icon">🔔</span>
          <h3 class="vip-notify-modal__title">${title}</h3>
        </div>
        <p class="vip-notify-modal__subtitle">${subtitle}</p>
        <div class="vip-notify-modal__message" id="vip-notify-message"></div>
        <div class="vip-notify-modal__input-wrapper">
          <input type="email" 
                 class="vip-notify-modal__input" 
                 id="vip-notify-email"
                 placeholder="your@email.com"
                 autocomplete="email">
          <div class="vip-notify-modal__error" id="vip-notify-error">Please enter a valid email address</div>
        </div>
        <div class="vip-notify-modal__buttons">
          <button class="vip-notify-modal__btn vip-notify-modal__btn--cancel" id="vip-notify-cancel">Cancel</button>
          <button class="vip-notify-modal__btn vip-notify-modal__btn--submit" id="vip-notify-submit">${buttonText}</button>
        </div>
      </div>
    `;
    
    document.body.appendChild(modal);
    
    // Trigger animation
    requestAnimationFrame(() => {
      modal.classList.add('active');
    });
    
    // Get elements
    const emailInput = document.getElementById('vip-notify-email');
    const submitBtn = document.getElementById('vip-notify-submit');
    const cancelBtn = document.getElementById('vip-notify-cancel');
    const errorEl = document.getElementById('vip-notify-error');
    const messageEl = document.getElementById('vip-notify-message');
    
    // Focus input
    setTimeout(() => emailInput.focus(), 100);
    
    // Close modal function
    const closeModal = () => {
      modal.classList.remove('active');
      setTimeout(() => modal.remove(), 200);
    };
    
    // Cancel button
    cancelBtn.addEventListener('click', closeModal);
    
    // Click outside to close
    modal.addEventListener('click', (e) => {
      if (e.target === modal) closeModal();
    });
    
    // Escape key to close
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        closeModal();
        document.removeEventListener('keydown', handleEscape);
      }
    };
    document.addEventListener('keydown', handleEscape);
    
    // Clear error on input
    emailInput.addEventListener('input', () => {
      emailInput.classList.remove('error');
      errorEl.classList.remove('visible');
    });
    
    // Submit handler
    const handleSubmit = async () => {
      const email = emailInput.value.trim();
      
      // Validate email
      if (!email || !email.includes('@') || !email.includes('.')) {
        emailInput.classList.add('error');
        errorEl.classList.add('visible');
        emailInput.focus();
        return;
      }
      
      // Show loading state
      submitBtn.classList.add('loading');
      submitBtn.disabled = true;
      
      try {
        // Call appropriate submit method based on timer type
        let success = false;
        let message = '';
        
        if (this.config.timerType === 'vip-special-event') {
          const result = await this.submitSpecialEventSubscribeAsync(email);
          success = result.success;
          message = result.message;
        } else if (['vip-full-moon', 'vip-new-moon'].includes(this.config.timerType)) {
          const result = await this.submitMoonNotifyRequestAsync(email);
          success = result.success;
          message = result.message;
        } else {
          const result = await this.submitNotifyRequestAsync(email);
          success = result.success;
          message = result.message;
        }
        
        // Show result message
        messageEl.textContent = message;
        messageEl.className = `vip-notify-modal__message ${success ? 'success' : 'error'}`;
        
        if (success) {
          // Hide input and buttons, show only message
          document.querySelector('.vip-notify-modal__input-wrapper').style.display = 'none';
          submitBtn.style.display = 'none';
          cancelBtn.textContent = 'Close';
          
          // Auto-close after 3 seconds
          setTimeout(closeModal, 3000);
        } else {
          // Reset button state
          submitBtn.classList.remove('loading');
          submitBtn.disabled = false;
        }
      } catch (error) {
        console.error('Notify submission error:', error);
        messageEl.textContent = 'Something went wrong. Please try again.';
        messageEl.className = 'vip-notify-modal__message error';
        submitBtn.classList.remove('loading');
        submitBtn.disabled = false;
      }
    };
    
    // Submit button click
    submitBtn.addEventListener('click', handleSubmit);
    
    // Enter key to submit
    emailInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') handleSubmit();
    });
  }
  
  /**
   * Submit special event subscribe - async version for modal
   */
  async submitSpecialEventSubscribeAsync(email) {
    try {
      const customerId = window.Shopify?.customer?.id || null;
      
      const response = await fetch('https://vip-counter-ne1mqgkw4-curts-projects-3ad527c5.vercel.app/api/special-event-subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), customerId })
      });

      const result = await response.json();
      
      if (response.ok) {
        return { success: true, message: "✓ You're subscribed! We'll email you when the next special event is announced." };
      } else {
        return { success: false, message: result.error || 'Please try again.' };
      }
    } catch (error) {
      console.error('Error subscribing to special events:', error);
      return { success: false, message: 'Something went wrong. Please try again.' };
    }
  }
  
  /**
   * Submit moon notify request - async version for modal
   */
  async submitMoonNotifyRequestAsync(email) {
    try {
      const response = await fetch('https://vip-counter-ne1mqgkw4-curts-projects-3ad527c5.vercel.app/api/moon-reminder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          timerType: this.config.timerType,
          productIds: [this.product.id],
          variantIds: [this.product.selected_or_first_available_variant?.id].filter(Boolean)
        })
      });

      const result = await response.json();
      
      if (response.ok) {
        return { success: true, message: "✓ You're all set! We'll email you when the next moon event opens." };
      } else {
        return { success: false, message: result.error || 'Please try again.' };
      }
    } catch (error) {
      console.error('Error submitting moon notification:', error);
      return { success: false, message: 'Something went wrong. Please try again.' };
    }
  }
  
  /**
   * Submit VIP notify request - async version for modal
   */
  async submitNotifyRequestAsync(email) {
    try {
      const response = await fetch('https://vip-counter-ne1mqgkw4-curts-projects-3ad527c5.vercel.app/api/vip-reminder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          timerType: this.config.timerType || 'vip-daily',
          productIds: [this.product.id],
          variantIds: [this.product.selected_or_first_available_variant?.id].filter(Boolean)
        })
      });

      const result = await response.json();
      
      if (response.ok) {
        return { success: true, message: "✓ You're all set! We'll email you when VIP opens." };
      } else {
        return { success: false, message: result.error || 'Please try again.' };
      }
    } catch (error) {
      console.error('VIP reminder error:', error);
      return { success: false, message: 'Something went wrong. Please try again.' };
    }
  }
  
  /**
   * Submit special event subscribe request (customer tagging)
   */
  async submitSpecialEventSubscribe(email) {
    try {
      // Check if customer is logged in (Shopify customer object)
      const customerId = window.Shopify?.customer?.id || null;
      
      const response = await fetch('https://vip-counter-ne1mqgkw4-curts-projects-3ad527c5.vercel.app/api/special-event-subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim(),
          customerId: customerId
        })
      });

      const result = await response.json();
      
      if (response.ok) {
        alert('✓ Great! You\'re subscribed to special event notifications. We\'ll email you when the next special event is announced.');
      } else {
        alert('Error: ' + (result.error || 'Please try again.'));
      }
    } catch (error) {
      console.error('Error subscribing to special events:', error);
      alert('Something went wrong. Please try again.');
    }
  }
  
  /**
   * Submit moon notify request to backend
   */
  async submitMoonNotifyRequest(email) {
    try {
      const response = await fetch('https://vip-counter-ne1mqgkw4-curts-projects-3ad527c5.vercel.app/api/moon-reminder', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim(),
          timerType: this.config.timerType,
          productIds: [this.product.id],
          variantIds: [this.product.selected_or_first_available_variant?.id].filter(Boolean)
        })
      });

      const result = await response.json();
      
      if (response.ok) {
        alert('✓ Great! We\'ll email you when the next moon event opens.');
      } else {
        alert('Error: ' + (result.error || 'Please try again.'));
      }
    } catch (error) {
      console.error('Error submitting moon notification:', error);
      alert('Something went wrong. Please try again.');
    }
  }

  /**
   * Submit notify request to backend (daily VIP)
   */
  async submitNotifyRequest(email) {
    try {
      const response = await fetch('https://vip-counter-ne1mqgkw4-curts-projects-3ad527c5.vercel.app/api/vip-reminder', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim(),
          timerType: this.config.timerType || 'vip-daily',
          productIds: [this.product.id],
          variantIds: [this.product.selected_or_first_available_variant?.id].filter(Boolean)
        })
      });

      const result = await response.json();
      
      if (response.ok) {
        alert('✓ Great! We\'ll email you when VIP opens.');
      } else {
        alert('Error: ' + (result.error || 'Please try again.'));
      }
    } catch (error) {
      console.error('VIP reminder error:', error);
      alert('Something went wrong. Please try again.');
    }
  }

  /**
   * Render availability information
   */
  async renderAvailability() {
    const currentStock = await this.getCurrentStock();
    
    // Use global pool total from shop metafields (primary source)
    const poolTotal = this.globalPoolTotal || this.config.quantityTotal || 5;
    
    // Check if this is an event-based timer
    const isEventBased = ['vip-full-moon', 'vip-new-moon', 'vip-special-event'].includes(this.config.timerType);
    
    // Only show "Limited availability" if we have no pool data AND quantityTotal is explicitly 0
    // Otherwise, use shop metafield data even if quantityTotal is 0
    const hasPoolData = this.globalPoolTotal && this.globalPoolTotal > 0;
    const shouldShowLimited = !hasPoolData && this.config.quantityTotal === 0;
    
    // Return plain text for inline display on product page
    if (this.isCart) {
      if (shouldShowLimited) {
        return {
          html: '<div class="vip-timer__availability">Limited availability</div>',
          label: 'Limited availability',
          value: ''
        };
      } else if (currentStock <= 0) {
        // ✅ FIX: Consistent "Sold Out" format for ALL timer types
        return {
          html: '<div class="vip-timer__availability vip-timer__availability--sold-out">Sold Out</div>',
          label: 'VIP',
          value: 'Sold Out'
        };
      } else {
        return {
          html: `<div class="vip-timer__availability">${currentStock}/${poolTotal} left today</div>`,
          label: `${currentStock}/${poolTotal} left today`,
          value: ''
        };
      }
    } else {
      if (shouldShowLimited) {
        return {
          html: 'Limited availability',
          label: 'Limited availability',
          value: ''
        };
      } else if (currentStock <= 0) {
        // ✅ FIX: Consistent "Sold Out" format for ALL timer types
        return {
          html: 'Sold Out',
          label: 'VIP',
          value: 'Sold Out'
        };
      } else {
        // Different text for event-based vs daily timers
        if (isEventBased) {
          return {
            html: `Only ${currentStock} left`,
            label: 'Only',
            value: `${currentStock} left`
          };
        } else {
          // Daily timers (VIP spells, VIP tarot) - original behavior
          return {
            html: `Only ${currentStock} available`,
            label: 'Only',
            value: `${currentStock} available`
          };
        }
      }
    }
  }

  /**
   * Force update VIP button states (bypasses change detection)
   */
  async forceUpdateVipButtonStates() {
    // console.log('🔧 Force updating VIP button states...');
    
    // Skip button updates in cart mode
    if (this.isCart) {
      return;
    }
    
    const status = this.calculateWindowStatus();
    const vipInputs = document.querySelectorAll('input[value="VIP"]');
    const vipLabels = document.querySelectorAll('input[value="VIP"] + label');
    const currentStock = await this.getCurrentStock();
    
    // For event-based timers, 'open' and 'event_day' are both available
    const isAvailable = (status.status === 'open' || status.status === 'event_day') && currentStock > 0;
    
    // console.log('🔧 Force applying VIP classes:', { isAvailable, status: status.status, stock: currentStock });
    
    vipInputs.forEach(input => {
      // Clear existing classes first
      input.classList.remove('vip-available', 'vip-unavailable');
      
      // Apply correct class
      if (isAvailable) {
        input.classList.add('vip-available');
      } else {
        input.classList.add('vip-unavailable');
      }
    });
    
    vipLabels.forEach(label => {
      // Clear existing classes first
      label.classList.remove('vip-available', 'vip-unavailable');
      
      // Apply correct class and attributes
      if (isAvailable) {
        label.classList.add('vip-available');
        label.removeAttribute('title');
      } else {
        label.classList.add('vip-unavailable');
        label.removeAttribute('title');
      }
    });
    
    // Update our tracking
    this.lastVipAvailability = {
      available: isAvailable,
      stock: currentStock,
      status: status.status
    };
  }

  /**
   * Update VIP button states based on availability
   */
  async updateVipButtonStates() {
    // Skip button updates in cart mode
    if (this.isCart) {
      return;
    }
    
    const status = this.calculateWindowStatus();
    const vipInputs = document.querySelectorAll('input[value="VIP"]');
    const vipLabels = document.querySelectorAll('input[value="VIP"] + label');
    const currentStock = await this.getCurrentStock();
    
    // For event-based timers, 'open' and 'event_day' are both available
    const isAvailable = (status.status === 'open' || status.status === 'event_day') && currentStock > 0;
    
    // Check current state
    const currentAvailability = vipInputs.length > 0 ? vipInputs[0].classList.contains('vip-available') : false;
    const hasAnyVipClass = vipInputs.length > 0 ? (vipInputs[0].classList.contains('vip-available') || vipInputs[0].classList.contains('vip-unavailable')) : false;
    
    // Store the last known availability state to detect real changes
    if (!this.lastVipAvailability) {
      this.lastVipAvailability = { available: null, stock: null, status: null };
    }
    
    const statusChanged = this.lastVipAvailability.status !== status.status;
    const stockChanged = this.lastVipAvailability.stock !== currentStock;
    const availabilityChanged = this.lastVipAvailability.available !== isAvailable;
    
    // For debugging - let's see what's happening
    // console.log('🔍 VIP Button State Check:', {
    //   timerType: this.config.timerType,
    //   status: status.status,
    //   currentStock,
    //   isAvailable,
    //   currentAvailability,
    //   hasAnyVipClass,
    //   statusChanged,
    //   stockChanged,
    //   availabilityChanged,
    //   shouldUpdate: statusChanged || stockChanged || availabilityChanged || !hasAnyVipClass
    // });
    
    // Only update if there's a real change in timer status/stock, or if classes aren't set yet
    if (!statusChanged && !stockChanged && !availabilityChanged && hasAnyVipClass) {
      // No real changes, don't interfere with user interaction
      return;
    }
    
    // Update our tracking
    this.lastVipAvailability = {
      available: isAvailable,
      stock: currentStock,
      status: status.status
    };
    
    debugLog('VIP availability changed:', { from: currentAvailability, to: isAvailable, status: status.status, stock: currentStock });
    
    vipInputs.forEach(input => {
      // Don't interfere with user selection - only update classes, not checked state
      if ((status.status === 'open' || status.status === 'event_day') && currentStock > 0) {
        // VIP is available and in stock
        input.disabled = false;
        input.classList.remove('vip-unavailable');
        input.classList.add('vip-available');
      } else if ((status.status === 'open' || status.status === 'event_day') && currentStock <= 0) {
        // VIP is available but sold out - keep clickable to view image
        input.disabled = false;
        input.classList.remove('vip-available');
        input.classList.add('vip-unavailable');
      } else {
        // VIP is not available - keep clickable to view image and extras
        input.disabled = false;
        input.classList.remove('vip-available');
        input.classList.add('vip-unavailable');
      }
    });
    
    vipLabels.forEach(label => {
      if ((status.status === 'open' || status.status === 'event_day') && currentStock > 0) {
        // VIP is available and in stock
        label.classList.remove('vip-unavailable');
        label.classList.add('vip-available');
        label.removeAttribute('title');
      } else if ((status.status === 'open' || status.status === 'event_day') && currentStock <= 0) {
        // VIP is available but sold out
        label.classList.remove('vip-available');
        label.classList.add('vip-unavailable');
        label.setAttribute('title', 'VIP sold out');
      } else {
        // VIP is closed
        label.classList.remove('vip-available');
        label.classList.add('vip-unavailable');
        label.removeAttribute('title');
      }
    });
    
    // Don't auto-switch on product page - let customers click VIP to see the image/details
    // The modal will handle the Premium switch when they try to add to cart
  }

  /**
   * Switch to Premium variant when VIP is unavailable
   */
  switchToPremiumVariant() {
    const premiumButton = document.querySelector('input[value="Premium"]');
    if (premiumButton) {
      debugLog('VIP unavailable, switching to Premium variant');
      premiumButton.checked = true;
      premiumButton.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * Block VIP selection if currently selected but unavailable
   */
  blockVipSelection(status) {
    // For event-based timers, 'open' and 'event_day' are both available
    const isAvailable = (status.status === 'open' || status.status === 'event_day');
    const currentlySelectedVip = document.querySelector('input[value="VIP"]:checked, .variant-picker__option[data-value="VIP"].selected');
    
    if (currentlySelectedVip && !isAvailable) {
      // Find the first available non-VIP variant
      const standardButton = document.querySelector('input[value="Standard"], .variant-picker__option[data-value="Standard"]');
      const premiumButton = document.querySelector('input[value="Premium"], .variant-picker__option[data-value="Premium"]');
      
      // Prefer Premium over Standard if available
      const fallbackButton = premiumButton || standardButton;
      
      if (fallbackButton) {
        debugLog('VIP unavailable, switching to fallback variant');
        
        // Trigger variant change
        if (fallbackButton.type === 'radio') {
          fallbackButton.checked = true;
          fallbackButton.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          fallbackButton.click();
        }
        
        // Show notification
        this.showVipUnavailableNotification(status);
      }
    }
  }

  /**
   * Show notification when VIP becomes unavailable
   */
  showVipUnavailableNotification(status) {
    // Remove any existing notification
    const existingNotification = document.querySelector('.vip-notification');
    if (existingNotification) {
      existingNotification.remove();
    }
    
    let message = '';
    switch (status.status) {
      case 'closed_opens_later':
        message = `VIP ordering opens in ${this.formatTimeRemaining(status.timeRemaining)}`;
        break;
      case 'closed_opens_tomorrow':
        message = `VIP ordering opens tomorrow in ${this.formatTimeRemaining(status.timeRemaining)}`;
        break;
      case 'weekend_blocked':
        message = 'VIP ordering unavailable on weekends';
        break;
    }
    
    const notification = document.createElement('div');
    notification.className = 'vip-notification';
    notification.innerHTML = `
      <div class="vip-notification__content">
        <span class="vip-notification__icon">⏰</span>
        <span class="vip-notification__text">${message}</span>
        <button class="vip-notification__close" onclick="this.parentElement.parentElement.remove()">×</button>
      </div>
    `;
    
    // Insert after the timer
    const timerElement = document.querySelector('.vip-timer');
    if (timerElement) {
      timerElement.insertAdjacentElement('afterend', notification);
    }
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      if (notification.parentElement) {
        notification.remove();
      }
    }, 5000);
  }

  /**
   * Start the countdown timer
   * Uses requestAnimationFrame for smoother, more efficient updates
   */
  startCountdown() {
    let buttonUpdateCounter = 0;
    let lastUpdateTime = performance.now();
    
    const animate = (currentTime) => {
      // Calculate elapsed time since last update
      const elapsed = currentTime - lastUpdateTime;
      
      // Update every ~1 second
      if (elapsed >= 1000) {
        if (this.isVisible) {
          this.renderTimerOnly(); // Only update timer display, not button states
          
          // For event-based timers, only update button states every 10 seconds
          // For daily timers, update every 5 seconds (they change more frequently)
          const isEventBased = ['vip-full-moon', 'vip-new-moon', 'vip-special-event'].includes(this.config.timerType);
          const updateFrequency = isEventBased ? 10 : 5;
          
          // Only update button states occasionally, and only if availability might have changed
          buttonUpdateCounter++;
          if (buttonUpdateCounter >= updateFrequency) {
            // For event-based timers, availability rarely changes, so check less often
            if (!isEventBased || buttonUpdateCounter >= 30) { // Event-based: every 30 seconds
              this.updateVipButtonStates();
            }
            buttonUpdateCounter = 0;
          }
        }
        
        // Reset timer (use elapsed to prevent drift)
        lastUpdateTime = currentTime - (elapsed % 1000);
      }
      
      // Continue animation loop
      this.updateInterval = requestAnimationFrame(animate);
    };
    
    // Start the animation loop
    this.updateInterval = requestAnimationFrame(animate);
  }

  /**
   * Setup visibility observer to pause updates when not visible
   */
  setupVisibilityObserver() {
    if ('IntersectionObserver' in window) {
      this.visibilityObserver = new IntersectionObserver((entries) => {
        this.isVisible = entries[0].isIntersecting;
      });
      this.visibilityObserver.observe(this.element);
    }
  }

  /**
   * Cleanup - properly removes all event listeners and timers
   * Important for cart page where timers are destroyed/recreated on updates
   */
  destroy() {
    // Cancel animation frame
    if (this.updateInterval) {
      cancelAnimationFrame(this.updateInterval);
      this.updateInterval = null;
    }
    
    // Clear debounce timeout
    if (this.vipUpdateTimeout) {
      clearTimeout(this.vipUpdateTimeout);
      this.vipUpdateTimeout = null;
    }
    
    // Remove event listeners (using stored bound references)
    if (this.boundDecrementHandler) {
      document.removeEventListener('vip:decrement-stock', this.boundDecrementHandler);
    }
    if (this.boundVariantChangeHandler) {
      document.removeEventListener('variant:change', this.boundVariantChangeHandler);
    }
    if (this.boundInputChangeHandler) {
      document.removeEventListener('change', this.boundInputChangeHandler);
    }
    
    // Disconnect visibility observer
    if (this.visibilityObserver) {
      this.visibilityObserver.disconnect();
      this.visibilityObserver = null;
    }
    
    // Clear element reference
    if (this.element) {
      this.element._vipTimerInstance = null;
    }
    
    debugLog('VipTimer destroyed and cleaned up');
  }
}

// Export for use in other files
window.VipTimer = VipTimer;
